import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { subscribeToNotifications } from '../services/notificationBus';

const GlobalNotificationContext = createContext(null);

const DEFAULT_DURATION_MS = 6000;
const MAX_MESSAGE_LENGTH = 280;

const normalizeMessage = (message, fallback) => {
  const raw = (message || '').toString().trim();
  if (!raw) {
    return fallback;
  }
  if (raw.length <= MAX_MESSAGE_LENGTH) {
    return raw;
  }
  return `${raw.slice(0, MAX_MESSAGE_LENGTH)}...`;
};

export const GlobalNotificationProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [activeNotification, setActiveNotification] = useState(null);
  const [open, setOpen] = useState(false);

  const enqueueNotification = useCallback((notification) => {
    const severity = notification?.severity || 'info';
    const fallbackBySeverity = {
      success: 'Action completed successfully.',
      warning: 'Please review this warning.',
      error: 'Something went wrong. Please try again.',
      info: 'Update available.'
    };

    const safeNotification = {
      severity,
      message: normalizeMessage(notification?.message, fallbackBySeverity[severity] || fallbackBySeverity.info),
      duration: Number(notification?.duration) > 0 ? Number(notification.duration) : DEFAULT_DURATION_MS
    };

    setQueue((previousQueue) => [...previousQueue, safeNotification]);
  }, []);

  useEffect(() => {
    if (!activeNotification && queue.length > 0) {
      setActiveNotification(queue[0]);
      setQueue((previousQueue) => previousQueue.slice(1));
      setOpen(true);
    }
  }, [activeNotification, queue]);

  useEffect(() => {
    const unsubscribe = subscribeToNotifications((notification) => {
      enqueueNotification(notification);
    });

    return unsubscribe;
  }, [enqueueNotification]);

  useEffect(() => {
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const message = reason?.message || reason?.toString?.() || '';
      const isNetworkError = /network|failed to fetch|load failed|networkerror/i.test(message);

      enqueueNotification({
        severity: 'error',
        message: isNetworkError
          ? 'Network connection was lost. Please check your connection and try again.'
          : 'Unexpected error occurred. Please retry the action.'
      });
    };

    const onWindowError = () => {
      enqueueNotification({
        severity: 'error',
        message: 'Unexpected runtime error occurred. Please refresh the page and try again.'
      });
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onWindowError);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onWindowError);
    };
  }, [enqueueNotification]);

  const closeNotification = useCallback((_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  const onExited = useCallback(() => {
    setActiveNotification(null);
  }, []);

  const showNotification = useCallback((message, options = {}) => {
    enqueueNotification({
      message,
      severity: options.severity || 'info',
      duration: options.duration
    });
  }, [enqueueNotification]);

  const showError = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'error' });
  }, [showNotification]);

  const showSuccess = useCallback((message, options = {}) => {
    showNotification(message, { ...options, severity: 'success' });
  }, [showNotification]);

  const contextValue = useMemo(() => ({
    showNotification,
    showError,
    showSuccess
  }), [showNotification, showError, showSuccess]);

  return (
    <GlobalNotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={activeNotification?.duration || DEFAULT_DURATION_MS}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionProps={{ onExited }}
      >
        <Alert
          severity={activeNotification?.severity || 'info'}
          variant="filled"
          onClose={closeNotification}
          sx={{ width: '100%' }}
        >
          {activeNotification?.message || ''}
        </Alert>
      </Snackbar>
    </GlobalNotificationContext.Provider>
  );
};

export const useGlobalNotification = () => {
  const context = useContext(GlobalNotificationContext);
  if (!context) {
    throw new Error('useGlobalNotification must be used within GlobalNotificationProvider');
  }
  return context;
};
